import {Component, OnInit} from '@angular/core';
import {UserPrivilegeService} from '../../../services/common/user-privilege/user-privilege.service';
import {LicenseFeaturesService} from '../../../services/common/license-features/license-features.service';
import {CallStudioApiService} from '../../../services/api/settings/call-flow-configurations/call-studio/call-studio-api.service';
import * as _ from 'lodash';
import {ComplementaryDataApiService} from '../../../services/api/settings/call-flow-configurations/complementary-data-api/complementary-data-api.service';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {ConfirmationDialogComponent} from '../../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {SnackBarService} from '../../../services/common/snack-bar/snack-bar.service';
import {SortableListDialogComponent} from '../../../dialogs/sortable-list-dialog/sortable-list-dialog.component';
import {AcDialogService} from 'ac-infra';
import {PolicyStudioDialogComponent} from './policy-studio-dialog/policy-studio-dialog.component';
import {RoleTypeEnum} from '../../../services/enums/role-type.enum';

@Component({
  selector: 'policy-studio',
  templateUrl: './policy-studio.component.html',
  styleUrls: ['./policy-studio.component.scss']
})
export class PolicyStudioComponent implements OnInit {

  isUnauthorized: boolean;
  isPolicyStudioDisabled: boolean;
  selection: any;
  policyStudioItems: any;
  policyStudioData: any;
  credentialsData: any;
  externalData: any;
  callItem: any;

  constructor(private dialog: MatDialog,
              private dialogService: AcDialogService,
              private snackBarService: SnackBarService,
              private userPrivilege: UserPrivilegeService,
              private callStudioApiService: CallStudioApiService,
              private complementaryDataApiService: ComplementaryDataApiService,
              private licenseFeaturesService: LicenseFeaturesService) { }

  ngOnInit(): void {
    this.isUnauthorized = !this.userPrivilege.isAuthorized(RoleTypeEnum.POLICY_STUDIO);

    this.licenseFeaturesService.isFeatureSupported('policyStudio').subscribe((isFeatureSupported) => {
      this.isPolicyStudioDisabled = !isFeatureSupported;
    });

    this.getExternalData();
    this.getData(null);
  }

  addCallItem(): void {
    const callItem = this.selection;
    const sentCallObject = {};

    const dataObject = this.populateData(_.cloneDeep(this.externalData));
    dataObject.pCons = dataObject.pCons.filter((pCon) => {
      return pCon.peerConnectionNodeType !== 'Virtual';
    });

    const dialogData: any = {
      dataObject,
      callItem: sentCallObject,
      isEditMode: false,
      selectedRule: this.selection,
      rules: this.policyStudioData
    };

    this.dialogService.open(PolicyStudioDialogComponent, {
      dialogData,
      title: 'Add policy studio rule',
      onSubmit: () => {
        if (!!callItem) {
          dialogData.callItem.id = callItem.id;
        }
        return this.callStudioApiService.getCallsItems().toPromise().then(callItems => {
          return this.callStudioApiService.addCallItem(dialogData.callItem, callItems).toPromise().then(item => {
            this.refresh(item);
          });
        });
      }
    });
  }

  editCallItem(event: Event, currentCallItem?): void {
    const callItem = _.isUndefined(currentCallItem) ? this.selection : currentCallItem;

    const dataObject = this.populateData(_.cloneDeep( this.externalData));
    const sentCallObject = {oldItem: callItem};

    const dialogData: any = {
      dataObject,
      callItem: sentCallObject,
      isEditMode: true,
      selectedRule: this.selection,
      rules: this.policyStudioData
    };
    this.dialogService.open(PolicyStudioDialogComponent, {
      noSubmitButton: this.isUnauthorized,
      dialogData,
      title: 'Edit policy studio rule',
      onSubmit: () => {
        if (!!callItem) {
          dialogData.dataObject.id = callItem.id;
        }

        dialogData.callItem.id = sentCallObject.oldItem.id;
        return this.callStudioApiService.editCallItem(dialogData.callItem).toPromise().then(item => {
          this.updatePolicyStudioList(item);
        });
      }
    });
  }

  deleteCallItem(event: Event, currentCallItem?): void {
    const policyStudioItem = _.isUndefined(currentCallItem) ? this.selection : currentCallItem;
    const confirmationMessage = `Are you sure you want to delete the item: "${policyStudioItem.name}" ?`;

    const callItemId = typeof policyStudioItem === 'object' ? policyStudioItem.id : policyStudioItem;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: confirmationMessage
      }
    });

    dialogRef.componentInstance.onApply.subscribe(() => {
      this.callStudioApiService.deleteCallItem(callItemId).subscribe(() => {
        dialogRef.close();
        this.snackBarService.display(`"${this.selection.name}" successfully deleted`);
        this.refresh(null);
      }, () => {});
    });
  }

  moveCallItem(event: Event, callItem?: any): void {
    const sentItem: any = _.isUndefined(callItem) ? this.selection : callItem;

    this.callStudioApiService.getCallsNames().subscribe(callItems => {
      const sentParameters: any = {prevId: -1, nextId: -1};
      const itemId = _.isObject(sentItem) as any ? sentItem.id : sentItem;
      const filteredList = this.filterItemsByType(callItems, sentItem.type);

      const dialogData = {
        groups: callItems,
        sentParameters,
        selectedGroup: itemId,
        filteredList
      };

      this.dialogService.open(SortableListDialogComponent, {
        dialogData,
        title: 'Move Routing Group',
        onSubmit: () => {
          sentParameters.selectedItemId = itemId;

          this.callStudioApiService.getCallsItems().subscribe(items => {
            this.callStudioApiService.moveCallItem(sentParameters, items).subscribe((item) => {
              this.refresh(null);
            });
          });
        }
      });
    });
  }

  refresh(selected?): void {
    this.getData(selected);
  }

  drop(event: CdkDragDrop<string[]>, viewPortInfo, type): void {
    if (this.isUnauthorized) {
      return;
    }

    const fromIndex = viewPortInfo.startIndexWithBuffer + event.previousIndex;
    const toIndex = viewPortInfo.startIndexWithBuffer + event.currentIndex;
    const currentList = type === 'credentials' ? this.credentialsData : this.policyStudioData;
    let prevId;
    let nextId;

    if (_.isEqual(toIndex, fromIndex)) {
      return;
    }

    const itemInList = this.policyStudioItems.find(item => item.id === currentList[toIndex].id);
    const currentItemIndex = this.policyStudioItems.findIndex(item => item.id === itemInList.id);

    if (fromIndex < toIndex) {
      prevId = this.policyStudioItems[currentItemIndex].id;
      nextId = this.policyStudioItems[currentItemIndex + 1] ? this.policyStudioItems[currentItemIndex + 1].id : -1;
    } else {
      prevId = this.policyStudioItems[currentItemIndex - 1] ? this.policyStudioItems[currentItemIndex - 1].id : -1;
      nextId = this.policyStudioItems[currentItemIndex].id;
    }

    this.moveItemInData(fromIndex, toIndex, currentList);
    const parameters = {
      nextId,
      prevId,
      selectedItemId: currentList[toIndex].id,
      currentIndex: toIndex
    };

    this.callStudioApiService.moveCallItem(parameters, this.policyStudioItems).subscribe(() => {
      this.callStudioApiService.getCallsItems().subscribe(callItems => {
        this.policyStudioItems = callItems;
      });
    }, () => {
      this.moveItemInData(toIndex, fromIndex, currentList);
    });
  }

  getData(selected): void {
    this.callStudioApiService.getCallsItems().subscribe(callsItems => {
      callsItems.forEach((callsItem) => {
        callsItem.adminStateBoolean = _.isEqual(callsItem.adminState, 'LOCKED');
        callsItem.open = false;
      });
      this.policyStudioItems = callsItems;
      this.policyStudioData = callsItems.filter(item => item.type !== 'CREDENTIALS');
      this.credentialsData = callsItems.filter(item => item.type === 'CREDENTIALS');

      this.selectItemById(selected);
    });
  }

  getExternalData(): void {
    this.complementaryDataApiService.getExternalData().then(dataObject => {
      this.externalData = dataObject;
    });
  }

  onDrag(): void {
    // WindowActionsService.clearWindowSelections();
  }

  selectHandler(selected): void {
    this.selection = selected === null ? undefined : selected;
  }

  onOpened(item): void {
    item.open = true;
    this.openHandler(item);
  }

  onClosed(item): void {
    item.open = false;
  }

  isSelected(item): boolean {
    return _.isEqual(this.selection, item);
  }

  stopEventPropagation(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
  }

  openHandler = (callItem) => {
    this.externalData.policyStudioRules = this.policyStudioData;
    this.callStudioApiService.populateCallItemFields(callItem, this.externalData);
  }

  toggleCallItemState(callItem, event: Event): void {
    this.stopEventPropagation(event);

    callItem.adminState = callItem.adminState === 'UNLOCKED' ? 'LOCKED' : 'UNLOCKED';

    this.callStudioApiService.editCallItem(callItem).subscribe(() => {
    }, () => {
      callItem.adminState = callItem.adminState === 'UNLOCKED' ? 'LOCKED' : 'UNLOCKED';
    });
  }

  moveItemInData(fromIndex, toIndex, currentList): void {
    moveItemInArray(currentList, fromIndex, toIndex);
    currentList = [...currentList];
  }

  private selectItemById(item): void {
    if (_.isNil(item)) {
      if ((this.policyStudioData.length === 0) &&  (this.credentialsData.length) === 0) {
        this.selection = null;
        return;
      }

      this.selection = (this.policyStudioData.length === 0) ?
        this.credentialsData[0] : this.policyStudioData[0];
    } else {
      this.selection = (item.type === 'CREDENTIALS') ?
        this.credentialsData.find(credentials => credentials.id === item.id) :
        this.policyStudioData.find(policyStudio => policyStudio.id === item.id);
    }
  }

  private filterItemsByType(callItems, type): any {
    return (type === 'CREDENTIALS') ? callItems.filter(i => i.type === 'CREDENTIALS') :
      callItems.filter(i => i.type !== 'CREDENTIALS');
  }

  private updatePolicyStudioList(itemToEdit): void {
    const currentList = (itemToEdit.type !== 'CREDENTIALS') ? this.policyStudioData : this.credentialsData;
    const index = currentList.findIndex(item => item.id === itemToEdit.id);

    if (index === -1) {
      this.refresh(itemToEdit);
      return;
    }

    itemToEdit.open = true;
    currentList[index] = itemToEdit;
    this.policyStudioItems = [...currentList];
    this.selectItemById(itemToEdit);
  }

  private populateData(fetchedItems): any {
    fetchedItems.pCons = fetchedItems.pCons.filter((pCon) => {
      return pCon.peerConnectionNodeType !== 'Virtual';
    });
    fetchedItems.nodes = this.getIdAndName(fetchedItems.nodes);
    fetchedItems.pCons = this.getIdAndNameForPcons(fetchedItems.pCons);
    fetchedItems.resourceGroups = this.getIdAndNameForResourceGroups(fetchedItems.resourceGroups);
    fetchedItems.numberPortability = this.getDataForNumberPortability(fetchedItems.numberPortability);
    fetchedItems.userGroups = this.getIdAndName(fetchedItems.userGroups);

    return fetchedItems;
  }

  private getIdAndName(data): any {
    data = data.map((item) => {
      return {id: item.id, name: item.name};
    });

    return data;
  }

  private getIdAndNameForPcons(data): any {
    data = data.map((item) => {
      return {id: item.id, name: item.name + ' (' + item.nodeName + ')'};
    });
    return data;
  }

  private getIdAndNameForResourceGroups(data): any {
    data = data.map((item) => {
      if (item.type === 'NODE') {
        return {id: item.id, name: item.name, type: 'Nodes Group'};
      } else if (item.type === 'PEER_CONNECTION') {
        return {id: item.id, name: item.name, type: 'Peer Connections Group'};
      }
    });
    return data;
  }

  private getDataForNumberPortability(data): any {
    data = data.map((item) => {
      return {id: item.id, name: item.name, type: item.numberPortabilityAgentType};
    });

    return data;
  }
}
